import React, { useState, useCallback } from 'react';
import {
  TouchableOpacity,
  TextInput as RNTextInput,
  Image,
  ScrollView,
  Alert,
  Modal,
  FlatList,
  Platform,
} from 'react-native';
import type { TextStyle, ViewStyle } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Text, useTheme } from '@tamagui/core';
import { YStack, XStack } from '@tamagui/stacks';

// Types
import type { RootStackScreenProps } from '@/types/navigation';

// Components
import { SvgBackground } from '@/components';

// API
import { useRegister } from '@/api/hooks/useAuth';

// Icons
import {
  Eye,
  EyeOff,
  Apple,
  Facebook,
  Chrome,
  CheckSquare,
  Square,
  Calendar,
  X,
} from 'lucide-react-native';

// Constants
import { COLORS, COMMON_STYLES } from '@/constants';

// Utils
import { validateEmail, validateNickname, validatePassword } from '@/utils';

// Assets
import { logo } from '@/assets';

type Props = RootStackScreenProps<'Register'>;

const RegisterScreen: React.FC<Props> = ({ navigation }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isWeb = Platform.OS === 'web';

  // State
  const [activeTab, setActiveTab] = useState<'login' | 'register'>('register');
  const [nickname, setNickname] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState<Date | null>(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  
  // Blur state for validation
  const [touchedFields, setTouchedFields] = useState<{
    nickname: boolean;
    email: boolean;
    password: boolean;
  }>({ nickname: false, email: false, password: false });

  const handleBlur = useCallback((field: 'nickname' | 'email' | 'password') => {
    setTouchedFields(prev => ({ ...prev, [field]: true }));
  }, []);
  
  // Date picker state
  const [selectedDay, setSelectedDay] = useState(1);
  const [selectedMonth, setSelectedMonth] = useState(1);
  const [selectedYear, setSelectedYear] = useState(2000);

  // API Hook
  const { mutate: register, isPending: isSubmitting } = useRegister({
    onSuccess: (data) => {
      setApiError(null);
      // Pass email and password to next step for login after phone verification
      navigation.navigate('RegisterStep2', { userId: data.id, email, password });
    },
    onError: (error) => {
      // Use description from API Platform ConstraintViolation if available
      const errorMessage = error.description || error.detail || error.message || t('errors.registrationFailed', 'Rejestracja nie powiodła się');
      setApiError(errorMessage);
    },
  });

  // Handlers
  const handleContinue = useCallback(() => {
    if (
      isSubmitting ||
      !validateNickname(nickname) ||
      !validateEmail(email) ||
      !validatePassword(password) ||
      !dateOfBirth ||
      !acceptedTerms
    )
      return;

    setApiError(null);
    register({
      email,
      password,
      nickName: nickname,
      dateOfBirth: dateOfBirth.toISOString().split('T')[0], // Format: YYYY-MM-DD
    });
  }, [isSubmitting, nickname, email, password, dateOfBirth, acceptedTerms, register]);

  const handleLogin = useCallback(() => {
    navigation.replace('Auth');
  }, [navigation]);

  const handleTabChange = useCallback((tab: 'login' | 'register') => {
    if (tab === 'login') {
      navigation.replace('Auth');
    } else {
      setActiveTab(tab);
    }
  }, [navigation]);

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword(prev => !prev);
  }, []);

  const toggleTermsAcceptance = useCallback(() => {
    setAcceptedTerms(prev => !prev);
  }, []);

  const openDatePicker = useCallback(() => {
    if (dateOfBirth) {
      setSelectedDay(dateOfBirth.getDate());
      setSelectedMonth(dateOfBirth.getMonth() + 1);
      setSelectedYear(dateOfBirth.getFullYear());
    }
    setShowDatePicker(true);
  }, [dateOfBirth]);

  const closeDatePicker = useCallback(() => {
    setShowDatePicker(false);
  }, []);

  const confirmDateSelection = useCallback(() => {
    const newDate = new Date(selectedYear, selectedMonth - 1, selectedDay);
    setDateOfBirth(newDate);
    setShowDatePicker(false);
  }, [selectedDay, selectedMonth, selectedYear]);

  const formatDateOfBirth = useCallback((date: Date | null): string => {
    if (!date) return '';
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  }, []);

  // Generate arrays for date picker
  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - 18 - i);

  // Validate age (minimum 18 years)
  const validateAge = useCallback((date: Date | null): boolean => {
    if (!date) return false;
    const today = new Date();
    const eighteenYearsAgo = new Date(
      today.getFullYear() - 18,
      today.getMonth(),
      today.getDate()
    );
    return date <= eighteenYearsAgo;
  }, []);

  const isAgeValid = validateAge(dateOfBirth);

  // Computed values
  const isFormValid =
    validateNickname(nickname) &&
    validateEmail(email) &&
    validatePassword(password) &&
    dateOfBirth !== null &&
    isAgeValid &&
    acceptedTerms;

  // Web layout - matching AuthScreen design
  if (isWeb) {
    return (
      <ScrollView 
        style={{ flex: 1, backgroundColor: theme.background?.val }} 
        contentContainerStyle={{ flexGrow: 1, paddingHorizontal: 20, paddingVertical: 20, justifyContent: 'space-between' }}
        showsVerticalScrollIndicator={false}
      >
        {/* Main content */}
        <YStack>
        {/* Title */}
        <YStack alignItems="center" marginBottom="$4">
          <Text fontSize={26} fontWeight="500" color="$color" textAlign="center">
            {t('screens.register.title', 'Zarejestruj się')}
          </Text>
          <Text fontSize={13} color="$color10" textAlign="center" marginTop={8} paddingHorizontal="$2">
            {t('screens.register.subtitle', 'Załóż konto i znajdź sobie towarzystwo')}
          </Text>
        </YStack>

        {/* Tab Switcher */}
        <XStack 
          marginBottom="$6"
          borderRadius={10}
          padding={4}
          borderWidth={1}
          borderColor="#e8e4e0"
          backgroundColor="#f8f7f6"
        >
          <TouchableOpacity
            onPress={() => handleTabChange('login')}
            style={getTabStyle(activeTab === 'login', theme)}
          >
            <Text 
              color={activeTab === 'login' ? COLORS.PRIMARY : '$color10'} 
              fontSize={13}
              fontWeight={activeTab === 'login' ? '600' : '400'}
            >
              {t('screens.auth.loginTab', 'Logowanie')}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => handleTabChange('register')}
            style={getTabStyle(activeTab === 'register', theme)}
          >
            <Text 
              color={activeTab === 'register' ? COLORS.PRIMARY : '$color10'} 
              fontSize={13}
              fontWeight={activeTab === 'register' ? '600' : '400'}
            >
              {t('screens.auth.registerTab', 'Rejestracja')}
            </Text>
          </TouchableOpacity>
        </XStack>

        {/* Form */}
        <YStack marginBottom="$2">
          <RNTextInput
            value={nickname}
            onChangeText={setNickname}
            onBlur={() => handleBlur('nickname')}
            placeholder={t('common.nicknamePlaceholder', 'Nazwa użytkownika')}
            placeholderTextColor={theme.color10?.val}
            style={getWebInputStyle(theme)}
          />
          {touchedFields.nickname && !validateNickname(nickname) && (
            <Text color={COLORS.ERROR} fontSize={10} marginTop={4}>
              {t('validation.nickname', 'Nazwa użytkownika musi mieć 2-20 znaków')}
            </Text>
          )}

          <RNTextInput
            value={email}
            onChangeText={setEmail}
            onBlur={() => handleBlur('email')}
            keyboardType="email-address"
            autoCapitalize="none"
            placeholder={t('common.emailPlaceholder', 'Email')}
            placeholderTextColor={theme.color10?.val}
            style={{...getWebInputStyle(theme), marginTop: 12}}
          />
          {touchedFields.email && !validateEmail(email) && (
            <Text color={COLORS.ERROR} fontSize={10} marginTop={4}>
              {t('validation.email', 'Podaj prawidłowy adres email')}
            </Text>
          )}

          <XStack position="relative" marginTop={12}>
            <RNTextInput
              value={password}
              onChangeText={setPassword}
              onBlur={() => handleBlur('password')}
              secureTextEntry={!showPassword}
              placeholder={t('common.passwordPlaceholder', 'Hasło (min. 12 znaków)')}
              placeholderTextColor={theme.color10?.val}
              style={getWebPasswordInputStyle(theme)}
            />
            <TouchableOpacity
              onPress={togglePasswordVisibility}
              style={getWebEyeButtonStyle()}
            >
              {showPassword ? (
                <EyeOff size={18} color={theme.color11?.val} />
              ) : (
                <Eye size={18} color={theme.color11?.val} />
              )}
            </TouchableOpacity>
          </XStack>
          {touchedFields.password && !validatePassword(password) && (
            <Text color={COLORS.ERROR} fontSize={10} marginTop={4}>
              {t('validation.password', 'Hasło musi mieć minimum 12 znaków')}
            </Text>
          )}

          {/* Date of Birth - native HTML date input for web */}
          <input
            type="date"
            value={dateOfBirth ? dateOfBirth.toISOString().split('T')[0] : ''}
            onChange={(e) => {
              const date = e.target.value ? new Date(e.target.value) : null;
              setDateOfBirth(date);
            }}
            max={new Date(new Date().getFullYear() - 18, new Date().getMonth(), new Date().getDate()).toISOString().split('T')[0]}
            style={{
              height: 46,
              borderWidth: 1,
              borderStyle: 'solid',
              borderColor: theme.borderColor?.val ?? '#e5e7eb',
              borderRadius: 10,
              paddingLeft: 16,
              paddingRight: 16,
              fontSize: 13,
              color: dateOfBirth ? theme.color?.val : theme.color10?.val,
              backgroundColor: theme.background?.val,
              fontFamily: 'Poppins',
              marginTop: 12,
              width: '100%',
              boxSizing: 'border-box',
            } as React.CSSProperties}
          />
          {dateOfBirth && !isAgeValid && (
            <Text color={COLORS.ERROR} fontSize={10} marginTop={4}>
              {t('validation.age', 'Musisz mieć ukończone 18 lat')}
            </Text>
          )}

          {/* Terms Checkbox */}
          <XStack alignItems="center" space="$2" marginTop={12} marginBottom={8}>
            <TouchableOpacity onPress={toggleTermsAcceptance}>
              {acceptedTerms ? (
                <CheckSquare size={18} color={COLORS.SUCCESS} />
              ) : (
                <Square size={18} color={theme.color8?.val} />
              )}
            </TouchableOpacity>
            <Text color="$color10" fontSize={11} flex={1}>
              {t(
                'screens.register.acceptTerms',
                'Akceptuję regulamin i politykę prywatności',
              )}
            </Text>
          </XStack>

          {/* API Error Display */}
          {apiError && (
            <Text color={COLORS.ERROR} fontSize={11} textAlign="center" marginTop={8}>
              {apiError}
            </Text>
          )}
        </YStack>

        <TouchableOpacity
          activeOpacity={0.9}
          disabled={!isFormValid || isSubmitting}
          onPress={handleContinue}
          style={getWebButtonStyle(!isFormValid || isSubmitting)}
        >
          <Text color="white" fontSize={14} fontWeight="600">
            {isSubmitting
              ? t('common.loading', 'Ładowanie...')
              : t('common.continue', 'Kontynuuj')}
          </Text>
        </TouchableOpacity>

        <XStack justifyContent="center" marginTop={16}>
          <Text color="$color10" fontSize={11}>
            {t('screens.register.haveAccount', 'Posiadasz konto?')}{' '}
          </Text>
          <TouchableOpacity onPress={handleLogin}>
            <Text color={COLORS.PRIMARY} fontSize={11} fontWeight="500">
              {t('common.signIn', 'Zaloguj się')}
            </Text>
          </TouchableOpacity>
        </XStack>
        </YStack>

        {/* Progress Stepper - at bottom */}
        <YStack alignItems="center">
          <XStack space="$2" alignItems="center">
            <YStack width={8} height={8} borderRadius={4} backgroundColor={COLORS.PRIMARY} />
            <YStack width={8} height={8} borderRadius={4} borderWidth={1} borderColor="#ccc" />
            <YStack width={8} height={8} borderRadius={4} borderWidth={1} borderColor="#ccc" />
          </XStack>
          <Text color="$color10" fontSize={11} marginTop={8}>
            {t('screens.register.step1', 'Krok 1 z 3')}
          </Text>
        </YStack>
      </ScrollView>
    );
  }

  // Mobile layout - original design
  return (
    <YStack flex={1} backgroundColor="$background">
      {/* Header */}
      <XStack
        paddingTop="$8"
        paddingHorizontal="$4"
        paddingBottom="$3"
        justifyContent="space-between"
        alignItems="center"
        position="relative"
        zIndex={10}
      >
        <XStack space="$3" />
      </XStack>

      {/* Hero Section */}
      <YStack>
        <SvgBackground height={220}>
          <Image
            source={logo}
            style={COMMON_STYLES.logo}
          />

          <Text
            fontSize="$8"
            fontWeight="bold"
            color="white"
            textAlign="center"
            fontFamily="Poppins"
          >
            {t('screens.register.title', 'Zarejestruj się')}
          </Text>

          <Text
            fontSize="$6"
            color="white"
            opacity={0.9}
            textAlign="center"
            fontFamily="Poppins"
            maxWidth="80%"
          >
            {t(
              'screens.register.subtitle',
              'Załóż konto i znajdź sobie towarzystwo na każdą okazję',
            )}
          </Text>
        </SvgBackground>
      </YStack>

      {/* Form */}
      <ScrollView
        contentContainerStyle={scrollContentStyle}
        showsVerticalScrollIndicator={false}
        style={scrollStyle}
      >
        <YStack space="$4">
          <RNTextInput
            value={nickname}
            onChangeText={setNickname}
            placeholder={t('common.nicknamePlaceholder', 'Nazwa użytkownika')}
            placeholderTextColor={theme.color10?.val}
            style={getInputStyle(theme)}
          />

          <RNTextInput
            value={email}
            onChangeText={setEmail}
            keyboardType="email-address"
            autoCapitalize="none"
            placeholder={t('common.emailPlaceholder', 'Email')}
            placeholderTextColor={theme.color10?.val}
            style={getInputStyle(theme)}
          />

          <XStack position="relative">
            <RNTextInput
              value={password}
              onChangeText={setPassword}
              secureTextEntry={!showPassword}
              placeholder={t('common.passwordPlaceholder', 'Hasło')}
              placeholderTextColor={theme.color10?.val}
              style={getPasswordInputStyle(theme)}
            />
            <TouchableOpacity
              onPress={togglePasswordVisibility}
              style={getEyeButtonStyle()}
            >
              {showPassword ? (
                <EyeOff size={20} color={theme.color11?.val} />
              ) : (
                <Eye size={20} color={theme.color11?.val} />
              )}
            </TouchableOpacity>
          </XStack>

          {/* Date of Birth */}
          <TouchableOpacity onPress={openDatePicker} style={getDateInputStyle(theme)}>
            <XStack flex={1} alignItems="center" justifyContent="space-between">
              <Text color={dateOfBirth ? '$color' : '$color10'}>
                {dateOfBirth
                  ? formatDateOfBirth(dateOfBirth)
                  : t('common.dateOfBirthPlaceholder', 'Data urodzenia')}
              </Text>
              <Calendar size={20} color={theme.color11?.val} />
            </XStack>
          </TouchableOpacity>

          {/* Terms Checkbox */}
          <XStack alignItems="center" space="$3" paddingVertical="$2">
            <TouchableOpacity onPress={toggleTermsAcceptance}>
              <CheckSquare
                size={24}
                color={acceptedTerms ? COLORS.SUCCESS : theme.color8?.val}
                fill={acceptedTerms ? COLORS.SUCCESS : 'transparent'}
              />
            </TouchableOpacity>
            <Text color="$color10" fontSize="$3" flex={1}>
              {t(
                'screens.register.acceptTerms',
                'Akceptuję regulamin i politykę prywatności',
              )}
            </Text>
          </XStack>

          {/* API Error Display */}
          {apiError && (
            <Text color={COLORS.ERROR} fontSize="$3" textAlign="center">
              {apiError}
            </Text>
          )}

          <TouchableOpacity
            activeOpacity={0.9}
            disabled={!isFormValid || isSubmitting}
            onPress={handleContinue}
            style={getContinueButtonStyle(theme, !isFormValid || isSubmitting)}
          >
            <Text color="white" fontSize="$4" fontWeight="600">
              {t('common.continue', 'Kontynuuj')}
            </Text>
          </TouchableOpacity>

          <XStack justifyContent="center" space="$1">
            <Text color="$color10" fontSize="$3">
              {t('screens.register.haveAccount', 'Posiadasz konto?')}
            </Text>
            <TouchableOpacity onPress={handleLogin}>
              <Text color={COLORS.PRIMARY} fontSize="$3" fontWeight="500">
                {t('common.signIn', 'Zaloguj się')}
              </Text>
            </TouchableOpacity>
          </XStack>

          <YStack alignItems="center" space="$3" paddingTop="$3">
            <Text color={COLORS.PRIMARY} fontSize="$3">
              {t('screens.register.signUpWith', 'Zarejestruj się za pomocą')}
            </Text>
            <XStack space="$4" justifyContent="center">
              <TouchableOpacity style={getSocialButtonStyle(theme)}>
                <Apple size={24} color={theme.color?.val} />
              </TouchableOpacity>
              <TouchableOpacity style={getSocialButtonStyle(theme)}>
                <Facebook size={24} color={theme.color?.val} />
              </TouchableOpacity>
              <TouchableOpacity style={getSocialButtonStyle(theme)}>
                <Chrome size={24} color={theme.color?.val} />
              </TouchableOpacity>
            </XStack>
          </YStack>

          {/* Progress Indicator */}
          <YStack
            alignItems="center"
            space="$2"
            paddingTop="$4"
            paddingBottom="$6"
          >
            <Text color="$color10" fontSize="$3">
              {t('screens.register.step1', 'Krok 1 z 3')}
            </Text>
            <XStack space="$2">
              <YStack
                width={8}
                height={8}
                borderRadius={4}
                backgroundColor="$color10"
              />
              <YStack
                width={8}
                height={8}
                borderRadius={4}
                borderWidth={1}
                borderColor="$color8"
              />
              <YStack
                width={8}
                height={8}
                borderRadius={4}
                borderWidth={1}
                borderColor="$color8"
              />
            </XStack>
          </YStack>
        </YStack>
      </ScrollView>

      {/* Date Picker Modal */}
      <Modal
        visible={showDatePicker}
        transparent
        animationType="slide"
        onRequestClose={closeDatePicker}
      >
        <YStack flex={1} backgroundColor="rgba(0,0,0,0.5)" justifyContent="flex-end">
          <YStack backgroundColor="$background" borderTopLeftRadius={20} borderTopRightRadius={20} padding="$4">
            {/* Header */}
            <XStack justifyContent="space-between" alignItems="center" marginBottom="$4">
              <Text fontSize="$5" fontWeight="600" color="$color">
                {t('common.selectDateOfBirth', 'Wybierz datę urodzenia')}
              </Text>
              <TouchableOpacity onPress={closeDatePicker}>
                <X size={24} color={theme.color?.val} />
              </TouchableOpacity>
            </XStack>

            {/* Date Pickers */}
            <XStack height={200} justifyContent="space-between" marginBottom="$4">
              {/* Day */}
              <YStack flex={1} marginRight="$2">
                <Text fontSize="$3" color="$color10" textAlign="center" marginBottom="$2">
                  {t('common.day', 'Dzień')}
                </Text>
                <FlatList
                  data={days}
                  keyExtractor={(item) => `day-${item}`}
                  showsVerticalScrollIndicator={false}
                  style={getPickerListStyle(theme)}
                  renderItem={({ item }) => (
                    <TouchableOpacity
                      onPress={() => setSelectedDay(item)}
                      style={getPickerItemStyle(item === selectedDay, theme)}
                    >
                      <Text
                        color={item === selectedDay ? 'white' : '$color'}
                        textAlign="center"
                        fontWeight={item === selectedDay ? '600' : '400'}
                      >
                        {item.toString().padStart(2, '0')}
                      </Text>
                    </TouchableOpacity>
                  )}
                />
              </YStack>

              {/* Month */}
              <YStack flex={1} marginHorizontal="$1">
                <Text fontSize="$3" color="$color10" textAlign="center" marginBottom="$2">
                  {t('common.month', 'Miesiąc')}
                </Text>
                <FlatList
                  data={months}
                  keyExtractor={(item) => `month-${item}`}
                  showsVerticalScrollIndicator={false}
                  style={getPickerListStyle(theme)}
                  renderItem={({ item }) => (
                    <TouchableOpacity
                      onPress={() => setSelectedMonth(item)}
                      style={getPickerItemStyle(item === selectedMonth, theme)}
                    >
                      <Text
                        color={item === selectedMonth ? 'white' : '$color'}
                        textAlign="center"
                        fontWeight={item === selectedMonth ? '600' : '400'}
                      >
                        {item.toString().padStart(2, '0')}
                      </Text>
                    </TouchableOpacity>
                  )}
                />
              </YStack>

              {/* Year */}
              <YStack flex={1} marginLeft="$2">
                <Text fontSize="$3" color="$color10" textAlign="center" marginBottom="$2">
                  {t('common.year', 'Rok')}
                </Text>
                <FlatList
                  data={years}
                  keyExtractor={(item) => `year-${item}`}
                  showsVerticalScrollIndicator={false}
                  style={getPickerListStyle(theme)}
                  renderItem={({ item }) => (
                    <TouchableOpacity
                      onPress={() => setSelectedYear(item)}
                      style={getPickerItemStyle(item === selectedYear, theme)}
                    >
                      <Text
                        color={item === selectedYear ? 'white' : '$color'}
                        textAlign="center"
                        fontWeight={item === selectedYear ? '600' : '400'}
                      >
                        {item}
                      </Text>
                    </TouchableOpacity>
                  )}
                />
              </YStack>
            </XStack>

            {/* Confirm Button */}
            <TouchableOpacity
              onPress={confirmDateSelection}
              style={getConfirmButtonStyle()}
            >
              <Text color="white" fontSize="$4" fontWeight="600">
                {t('common.confirm', 'Potwierdź')}
              </Text>
            </TouchableOpacity>
          </YStack>
        </YStack>
      </Modal>
    </YStack>
  );
};

// Tab styles
function getTabStyle(isActive: boolean, theme: ReturnType<typeof useTheme>): ViewStyle {
  return {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: isActive ? '#ffffff' : 'transparent',
    alignItems: 'center',
    justifyContent: 'center',
  };
}

// Web styles
function getWebInputStyle(theme: ReturnType<typeof useTheme>): TextStyle {
  return {
    height: 46,
    borderWidth: 1,
    borderColor: theme.borderColor?.val ?? '#e5e7eb',
    borderRadius: 10,
    paddingHorizontal: 16,
    fontSize: 13,
    color: theme.color?.val,
    backgroundColor: theme.background?.val,
    fontFamily: 'Poppins',
  };
}

function getWebPasswordInputStyle(theme: ReturnType<typeof useTheme>): TextStyle {
  return {
    ...getWebInputStyle(theme),
    flex: 1,
    paddingRight: 48,
  };
}

function getWebEyeButtonStyle(): ViewStyle {
  return {
    position: 'absolute',
    right: 14,
    top: 14,
    padding: 2,
  };
}

function getWebDateInputStyle(theme: ReturnType<typeof useTheme>): ViewStyle {
  return {
    height: 46,
    borderWidth: 1,
    borderColor: theme.borderColor?.val ?? '#e5e7eb',
    borderRadius: 10,
    paddingHorizontal: 16,
    backgroundColor: theme.background?.val,
    justifyContent: 'center',
  };
}

function getWebButtonStyle(disabled: boolean): ViewStyle {
  return {
    height: 46,
    borderRadius: 10,
    backgroundColor: disabled ? COLORS.DISABLED : COLORS.PRIMARY,
    alignItems: 'center',
    justifyContent: 'center',
  };
}

function getWebSocialButtonStyle(theme: ReturnType<typeof useTheme>): ViewStyle {
  return {
    width: 42,
    height: 42,
    borderRadius: 21,
    borderWidth: 1,
    borderColor: theme.borderColor?.val ?? '#e5e7eb',
    backgroundColor: theme.background?.val,
    alignItems: 'center',
    justifyContent: 'center',
  };
}

// Mobile styles
function getInputStyle(theme: ReturnType<typeof useTheme>): TextStyle {
  return {
    ...COMMON_STYLES.input,
    borderColor: theme.borderColor?.val ?? '#e5e7eb',
    color: theme.color?.val,
    backgroundColor: theme.background?.val,
  };
}

function getPasswordInputStyle(theme: ReturnType<typeof useTheme>): TextStyle {
  return {
    ...getInputStyle(theme),
    flex: 1,
    paddingRight: 50,
  };
}

function getEyeButtonStyle(): ViewStyle {
  return {
    position: 'absolute',
    right: 16,
    top: 14,
    padding: 4,
  };
}

function getContinueButtonStyle(
  theme: ReturnType<typeof useTheme>,
  disabled: boolean,
): ViewStyle {
  return {
    ...COMMON_STYLES.button,
    backgroundColor: disabled ? COLORS.DISABLED : COLORS.PRIMARY,
    width: '100%',
  };
}

function getSocialButtonStyle(theme: ReturnType<typeof useTheme>): ViewStyle {
  return {
    ...COMMON_STYLES.socialButton,
    borderColor: theme.borderColor?.val ?? '#e5e7eb',
    backgroundColor: theme.background?.val,
  };
}

function getDateInputStyle(theme: ReturnType<typeof useTheme>): ViewStyle {
  return {
    ...COMMON_STYLES.input,
    borderColor: theme.borderColor?.val ?? '#e5e7eb',
    backgroundColor: theme.background?.val,
  };
}

function getPickerListStyle(theme: ReturnType<typeof useTheme>): ViewStyle {
  return {
    backgroundColor: theme.background?.val,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.borderColor?.val ?? '#e5e7eb',
  };
}

function getPickerItemStyle(isSelected: boolean, theme: ReturnType<typeof useTheme>): ViewStyle {
  return {
    paddingVertical: 12,
    paddingHorizontal: 8,
    backgroundColor: isSelected ? COLORS.PRIMARY : 'transparent',
    borderRadius: 4,
    marginVertical: 2,
    marginHorizontal: 4,
  };
}

function getConfirmButtonStyle(): ViewStyle {
  return {
    ...COMMON_STYLES.button,
    backgroundColor: COLORS.PRIMARY,
    width: '100%',
  };
}

// ScrollView styles
const scrollContentStyle = {
  paddingHorizontal: 16,
  paddingTop: 16,
  paddingBottom: 32,
};

const scrollStyle = {
  zIndex: 2,
};

export default RegisterScreen;
